import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import { TemplateModel } from '../../models/template.model';
import { IntegrationMappingModel, IntegrationTemplateCloneRequestModel } from '../../models/integration.model';
import { TemplateService } from '../../services/template.service';
import { IntegrationService } from '../../services/integration.service';

interface IntegrationRow {
  rowId: string;
  integrationId?: number;
  sourceId: string;
  kanseiId: string;
  dataType: string;
  comments: string;
  assignTemplateIds: number[];
}

type IntegrationType = 'kisha' | 'ewmr' | 'commonTags' | 'dashboard';
type DashboardTemplateCategory = 'template' | 'forms';

@Component({
  selector: 'app-integrations',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule, MultiSelectModule, DialogModule, ToastModule],
  providers: [MessageService],
  templateUrl: './integrations.html',
  styleUrl: './integrations.css'
})
export class IntegrationsComponent implements OnInit {
  activeIntegration: IntegrationType = 'kisha';

  kishaRows: IntegrationRow[] = [];
  ewmrRows: IntegrationRow[] = [];
  commonTagsRows: IntegrationRow[] = [];
  dashboardRows: IntegrationRow[] = [];

  kishaTemplates: TemplateModel[] = [];
  ewmrTemplates: TemplateModel[] = [];
  commonTagsTemplates: TemplateModel[] = [];
  dashboardTemplates: TemplateModel[] = [];
  dashboardTemplateCategory: DashboardTemplateCategory = 'template';
  readonly dashboardTemplateCategoryOptions = [
    { label: 'Template', value: 'template' as DashboardTemplateCategory },
    { label: 'Forms', value: 'forms' as DashboardTemplateCategory }
  ];
  selectedKishaTemplateId: number | null = null;
  selectedEwmrTemplateId: number | null = null;
  selectedCommonTagsTemplateId: number | null = null;
  selectedDashboardTemplateId: number | null = null;

  loadingKishaTemplates = false;
  loadingEwmrTemplates = false;
  loadingCommonTagsTemplates = false;
  loadingDashboardTemplates = false;
  loadingKishaMappings = false;
  loadingEwmrMappings = false;
  loadingCommonTagsMappings = false;
  loadingDashboardMappings = false;
  saving = false;
  cloning = false;
  showCloneDialog = false;
  cloneTargetTemplateIds: number[] = [];
  private loadedMappings: Record<IntegrationType, boolean> = {
    kisha: false,
    ewmr: false,
    commonTags: false,
    dashboard: false
  };

  constructor(
    private templateService: TemplateService,
    private integrationService: IntegrationService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.kishaRows = [this.createRow()];
    this.ewmrRows = [this.createRow()];
    this.commonTagsRows = [this.createRow()];
    this.dashboardRows = [this.createRow()];
    this.loadTemplates('kisha');
    this.loadIntegrationMappings('kisha');
  }

  setIntegration(type: IntegrationType): void {
    this.activeIntegration = type;
    this.ensureTrailingEmptyRow();
    if (type === 'kisha' && this.kishaTemplates.length === 0) {
      this.loadTemplates('kisha');
    }
    if (type === 'ewmr' && this.ewmrTemplates.length === 0) {
      this.loadTemplates('ewmr');
    }
    if (type === 'commonTags' && this.commonTagsTemplates.length === 0) {
      this.loadTemplates('commonTags');
    }
    if (type === 'dashboard' && this.dashboardTemplates.length === 0) {
      this.loadTemplates('dashboard');
    }
    if (!this.loadedMappings[type]) {
      this.loadIntegrationMappings(type);
    }
  }

  get sourceLabel(): string {
    if (this.activeIntegration === 'kisha') return 'Kisha ID';
    if (this.activeIntegration === 'ewmr') return 'EWMR/NCR ID';
    return '';
  }

  get idLabel(): string {
    return this.activeIntegration === 'commonTags' || this.activeIntegration === 'dashboard' ? 'Tag Id' : 'Navigator Id';
  }

  get showSourceColumn(): boolean {
    return this.activeIntegration !== 'commonTags' && this.activeIntegration !== 'dashboard';
  }

  get currentRows(): IntegrationRow[] {
    if (this.activeIntegration === 'kisha') return this.kishaRows;
    if (this.activeIntegration === 'ewmr') return this.ewmrRows;
    if (this.activeIntegration === 'dashboard') return this.dashboardRows;
    return this.commonTagsRows;
  }

  get currentTemplates(): TemplateModel[] {
    if (this.activeIntegration === 'kisha') return this.kishaTemplates;
    if (this.activeIntegration === 'ewmr') return this.ewmrTemplates;
    if (this.activeIntegration === 'dashboard') return this.dashboardTemplates;
    return this.commonTagsTemplates;
  }

  get selectedTemplateId(): number | null {
    if (this.activeIntegration === 'kisha') return this.selectedKishaTemplateId;
    if (this.activeIntegration === 'ewmr') return this.selectedEwmrTemplateId;
    if (this.activeIntegration === 'dashboard') return this.selectedDashboardTemplateId;
    return this.selectedCommonTagsTemplateId;
  }

  set selectedTemplateId(value: number | null) {
    if (this.activeIntegration === 'kisha') {
      this.selectedKishaTemplateId = value;
    } else if (this.activeIntegration === 'ewmr') {
      this.selectedEwmrTemplateId = value;
    } else if (this.activeIntegration === 'dashboard') {
      this.selectedDashboardTemplateId = value;
    } else {
      this.selectedCommonTagsTemplateId = value;
    }
  }

  get isLoadingTemplates(): boolean {
    if (this.activeIntegration === 'kisha') return this.loadingKishaTemplates;
    if (this.activeIntegration === 'ewmr') return this.loadingEwmrTemplates;
    if (this.activeIntegration === 'dashboard') return this.loadingDashboardTemplates;
    return this.loadingCommonTagsTemplates;
  }

  get isLoadingMappings(): boolean {
    if (this.activeIntegration === 'kisha') return this.loadingKishaMappings;
    if (this.activeIntegration === 'ewmr') return this.loadingEwmrMappings;
    if (this.activeIntegration === 'dashboard') return this.loadingDashboardMappings;
    return this.loadingCommonTagsMappings;
  }

  addRow(): void {
    this.currentRows.push(this.createRow());
  }

  deleteRow(index: number): void {
    const rows = this.currentRows;
    if (index < 0 || index >= rows.length) {
      return;
    }

    const row = rows[index];
    if (row.integrationId) {
      this.integrationService.deleteIntegrationMapping(row.integrationId).subscribe({
        next: () => {
          rows.splice(index, 1);
          if (rows.length === 0) {
            rows.push(this.createRow());
          }
          this.ensureTrailingEmptyRow();
          this.messageService.add({
            severity: 'success',
            summary: 'Deleted',
            detail: 'Integration row deleted successfully.'
          });
        },
        error: (error) => {
          console.error('Error deleting integration row:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete integration row.'
          });
        }
      });
      return;
    }

    rows.splice(index, 1);

    if (rows.length === 0) {
      rows.push(this.createRow());
    }

    this.ensureTrailingEmptyRow();
  }

  saveRows(): void {
    if (!this.selectedTemplateId || this.selectedTemplateId <= 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'Template Required',
        detail: 'Select a template to update mappings.'
      });
      return;
    }

    const rowsToSave = this.currentRows.filter((row) => !this.isRowEmpty(row));
    if (rowsToSave.length === 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'No Changes',
        detail: 'Enter at least one row to save.'
      });
      return;
    }

    const invalidRows = rowsToSave.filter((row) => !this.isRowValid(row));
    if (invalidRows.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid Rows',
        detail: 'Source ID, Navigator ID, and Data Type are required for all filled rows.'
      });
      return;
    }

    this.saving = true;
    const integrationType = this.toBackendIntegrationType(this.activeIntegration);
    const payload: IntegrationMappingModel[] = rowsToSave.map((row) => ({
        integrationId: row.integrationId,
        integrationType,
        sourceId: this.isSourceRequired(this.activeIntegration) ? row.sourceId.trim() : undefined,
        kanseiId: row.kanseiId.trim(),
        dataType: row.dataType.trim(),
        comments: row.comments.trim() || undefined,
        isActive: true,
        templateIds: row.assignTemplateIds || []
      }));

    this.integrationService.upsertIntegrationMappings(payload)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (response) => {
          const successful = response?.savedCount ?? 0;
          if (successful === 0) {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to save integration rows.'
            });
            return;
          }

          this.messageService.add({
            severity: successful === rowsToSave.length ? 'success' : 'warn',
            summary: successful === rowsToSave.length ? 'Saved' : 'Partial Save',
            detail: successful === rowsToSave.length
              ? 'Integration mappings saved successfully.'
              : `${successful} of ${rowsToSave.length} rows saved.`
          });

          this.loadIntegrationMappings(this.activeIntegration);
        },
        error: (error) => {
          console.error('Error saving integration mappings:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save integration mappings.'
          });
        }
      });
  }

  openCloneDialog(): void {
    if (!this.selectedTemplateId || this.selectedTemplateId <= 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'Template Required',
        detail: 'Select a source template first.'
      });
      return;
    }

    const rowsMissingIntegrationId = this.currentRows
      .filter((row) => this.isRowValid(row) && this.isTemplateMapped(row) && !row.integrationId);

    if (rowsMissingIntegrationId.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Save Required',
        detail: 'Save rows first before cloning mapped selections.'
      });
      return;
    }

    this.cloneTargetTemplateIds = [];
    this.showCloneDialog = true;
  }

  cloneSelections(): void {
    if (!this.selectedTemplateId || this.selectedTemplateId <= 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'Template Required',
        detail: 'Select a source template first.'
      });
      return;
    }

    if (!this.cloneTargetTemplateIds || this.cloneTargetTemplateIds.length === 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'Target Required',
        detail: 'Select at least one target template.'
      });
      return;
    }

    const payload: IntegrationTemplateCloneRequestModel = {
      integrationType: this.toBackendIntegrationType(this.activeIntegration),
      sourceTemplateId: this.selectedTemplateId,
      targetTemplateIds: this.cloneTargetTemplateIds,
      selectedIntegrationMappingIds: this.getSelectedIntegrationMappingIds()
    };

    this.cloning = true;
    this.integrationService.cloneTemplateAssociations(payload)
      .pipe(finalize(() => (this.cloning = false)))
      .subscribe({
        next: (result) => {
          this.showCloneDialog = false;
          this.cloneTargetTemplateIds = [];
          this.messageService.add({
            severity: 'success',
            summary: 'Cloned',
            detail: `Applied ${result.selectedMappingCount} mappings to ${result.targetTemplateCount} templates.`
          });
          this.loadIntegrationMappings(this.activeIntegration);
        },
        error: (error) => {
          console.error('Error cloning template associations:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to clone template associations.'
          });
        }
      });
  }

  get cloneTargetTemplateOptions(): TemplateModel[] {
    const sourceTemplateId = this.selectedTemplateId;
    return this.currentTemplates.filter((template) => template.templateId !== sourceTemplateId);
  }

  onTemplateChanged(): void {
    this.ensureTrailingEmptyRow();
  }

  onDashboardTemplateCategoryChanged(): void {
    if (this.activeIntegration !== 'dashboard') {
      return;
    }

    this.selectedDashboardTemplateId = null;
    this.dashboardTemplates = [];
    this.loadTemplates('dashboard');
    this.ensureTrailingEmptyRow();
  }

  isTemplateMapped(row: IntegrationRow): boolean {
    const templateId = this.selectedTemplateId;
    if (!templateId || templateId <= 0) {
      return false;
    }
    return (row.assignTemplateIds || []).includes(templateId);
  }

  onTemplateMappedChange(row: IntegrationRow, checked: boolean): void {
    const templateId = this.selectedTemplateId;
    if (!templateId || templateId <= 0) {
      return;
    }

    const existing = new Set(row.assignTemplateIds || []);
    if (checked) {
      existing.add(templateId);
    } else {
      existing.delete(templateId);
    }

    row.assignTemplateIds = Array.from(existing);
  }

  isAllMappedSelected(): boolean {
    const rows = this.getSelectableRows();
    if (rows.length === 0) {
      return false;
    }
    return rows.every((row) => this.isTemplateMapped(row));
  }

  isSomeMappedSelected(): boolean {
    const rows = this.getSelectableRows();
    if (rows.length === 0) {
      return false;
    }
    const selectedCount = rows.filter((row) => this.isTemplateMapped(row)).length;
    return selectedCount > 0 && selectedCount < rows.length;
  }

  toggleSelectAllMapped(checked: boolean): void {
    const rows = this.getSelectableRows();
    rows.forEach((row) => this.onTemplateMappedChange(row, checked));
  }

  onRowChanged(index: number): void {
    const rows = this.currentRows;
    if (index === rows.length - 1 && !this.isRowEmpty(rows[index])) {
      rows.push(this.createRow());
    }
  }

  onDataTypeBlur(row: IntegrationRow): void {
    row.dataType = (row.dataType || '').trim().toUpperCase();
  }

  trackByRow(_index: number, row: IntegrationRow): string {
    return row.rowId;
  }

  private loadTemplates(type: IntegrationType): void {
    const templateTypeId = this.getTemplateTypeId(type);
    if (type === 'kisha') {
      this.loadingKishaTemplates = true;
    } else if (type === 'ewmr') {
      this.loadingEwmrTemplates = true;
    } else if (type === 'dashboard') {
      this.loadingDashboardTemplates = true;
    } else {
      this.loadingCommonTagsTemplates = true;
    }

    this.templateService.getTemplateList(templateTypeId).subscribe({
      next: (response) => {
        const templates = response || [];
        if (type === 'kisha') {
          this.kishaTemplates = templates;
          if (!this.selectedKishaTemplateId && templates.length > 0) {
            this.selectedKishaTemplateId = templates[0].templateId ?? null;
          }
          this.loadingKishaTemplates = false;
        } else if (type === 'ewmr') {
          this.ewmrTemplates = templates;
          if (!this.selectedEwmrTemplateId && templates.length > 0) {
            this.selectedEwmrTemplateId = templates[0].templateId ?? null;
          }
          this.loadingEwmrTemplates = false;
        } else if (type === 'dashboard') {
          this.dashboardTemplates = templates;
          if (!this.selectedDashboardTemplateId && templates.length > 0) {
            this.selectedDashboardTemplateId = templates[0].templateId ?? null;
          }
          this.loadingDashboardTemplates = false;
        } else {
          this.commonTagsTemplates = templates;
          if (!this.selectedCommonTagsTemplateId && templates.length > 0) {
            this.selectedCommonTagsTemplateId = templates[0].templateId ?? null;
          }
          this.loadingCommonTagsTemplates = false;
        }
      },
      error: (error) => {
        console.error(`Error loading ${type.toUpperCase()} templates:`, error);
        if (type === 'kisha') {
          this.loadingKishaTemplates = false;
          this.kishaTemplates = [];
        } else if (type === 'ewmr') {
          this.loadingEwmrTemplates = false;
          this.ewmrTemplates = [];
        } else if (type === 'dashboard') {
          this.loadingDashboardTemplates = false;
          this.dashboardTemplates = [];
        } else {
          this.loadingCommonTagsTemplates = false;
          this.commonTagsTemplates = [];
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to load ${type.toUpperCase()} templates.`
        });
      }
    });
  }

  private loadIntegrationMappings(type: IntegrationType): void {
    const backendType = this.toBackendIntegrationType(type);
    if (type === 'kisha') {
      this.loadingKishaMappings = true;
    } else if (type === 'ewmr') {
      this.loadingEwmrMappings = true;
    } else if (type === 'dashboard') {
      this.loadingDashboardMappings = true;
    } else {
      this.loadingCommonTagsMappings = true;
    }

    this.integrationService.getIntegrationMappings(backendType).subscribe({
      next: (response) => {
        const rows = (response || []).map((item) => this.mapApiToRow(item));
        if (type === 'kisha') {
          this.kishaRows = rows;
          this.loadingKishaMappings = false;
        } else if (type === 'ewmr') {
          this.ewmrRows = rows;
          this.loadingEwmrMappings = false;
        } else if (type === 'dashboard') {
          this.dashboardRows = rows;
          this.loadingDashboardMappings = false;
        } else {
          this.commonTagsRows = rows;
          this.loadingCommonTagsMappings = false;
        }
        this.loadedMappings[type] = true;
        this.ensureTrailingEmptyRow();
      },
      error: (error) => {
        console.error(`Error loading ${type.toUpperCase()} mappings:`, error);
        if (type === 'kisha') {
          this.loadingKishaMappings = false;
          this.kishaRows = [this.createRow()];
        } else if (type === 'ewmr') {
          this.loadingEwmrMappings = false;
          this.ewmrRows = [this.createRow()];
        } else if (type === 'dashboard') {
          this.loadingDashboardMappings = false;
          this.dashboardRows = [this.createRow()];
        } else {
          this.loadingCommonTagsMappings = false;
          this.commonTagsRows = [this.createRow()];
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to load ${type.toUpperCase()} mappings.`
        });
      }
    });
  }

  private mapApiToRow(item: IntegrationMappingModel): IntegrationRow {
    return {
      rowId: this.createRowId(),
      integrationId: item.integrationId,
      sourceId: item.sourceId || '',
      kanseiId: item.kanseiId || '',
      dataType: item.dataType || '',
      comments: item.comments || '',
      assignTemplateIds: item.templateIds || []
    };
  }

  private toBackendIntegrationType(type: IntegrationType): string {
    if (type === 'commonTags') {
      return 'COMMON_TAGS';
    }
    if (type === 'dashboard') {
      return 'DASHBOARD';
    }
    return type.toUpperCase();
  }

  private getTemplateTypeId(type: IntegrationType): number {
    if (type === 'ewmr') {
      return 2;
    }

    if (type === 'dashboard') {
      return this.dashboardTemplateCategory === 'forms' ? 2 : 1;
    }

    return 1;
  }

  private ensureTrailingEmptyRow(): void {
    const rows = this.currentRows;
    if (rows.length === 0 || !this.isRowEmpty(rows[rows.length - 1])) {
      rows.push(this.createRow());
    }
  }

  private isRowEmpty(row: IntegrationRow): boolean {
    return (!this.isSourceRequired(this.activeIntegration) || !row.sourceId.trim())
      && !row.kanseiId.trim()
      && !row.dataType.trim()
      && !row.comments.trim();
  }

  isRowValid(row: IntegrationRow): boolean {
    return (!this.isSourceRequired(this.activeIntegration) || !!row.sourceId.trim())
      && !!row.kanseiId.trim()
      && !!row.dataType.trim();
  }

  private isSourceRequired(type: IntegrationType): boolean {
    return type !== 'commonTags' && type !== 'dashboard';
  }

  private getSelectedIntegrationMappingIds(): number[] {
    return this.currentRows
      .filter((row) => this.isRowValid(row) && this.isTemplateMapped(row) && !!row.integrationId)
      .map((row) => row.integrationId as number)
      .filter((id) => id > 0);
  }

  private getSelectableRows(): IntegrationRow[] {
    return this.currentRows.filter((row) => this.isRowValid(row));
  }

  private createRow(): IntegrationRow {
    return {
      rowId: this.createRowId(),
      sourceId: '',
      kanseiId: '',
      dataType: '',
      comments: '',
      assignTemplateIds: []
    };
  }

  private createRowId(): string {
    return `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
}
